import type { Prisma, PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { csvLine } from '../csv/csv.parser.js';
import {
  createParty,
  findDuplicates,
  ownScope,
  partyInclude,
  partyInputSchema,
  partyView,
  updateParty,
  type PartyInput,
} from './parties.service.js';
import { customFieldError } from './custom-fields.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const CLIENT = 'sales.client';
const VENDOR = 'purchases.vendor';

export function createPartiesRouter({ db, config }: Deps) {
  const router = Router();
  router.use(['/parties', '/tags', '/custom-fields'], authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const can = (req: Request, permission: string) =>
    permissionsGranted(req.auth!.permissions, permission);
  const need = (req: Request, permission: string) => {
    if (!can(req, permission))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };

  /** Which parties this user may see: by role permissions, then by own-data scope. */
  function visible(req: Request): Prisma.PartyWhereInput {
    const parts: Prisma.PartyWhereInput[] = [];
    if (can(req, `${CLIENT}.view`)) parts.push({ OR: [{ isClient: true }, { isProspect: true }] });
    if (can(req, `${VENDOR}.view`)) parts.push({ isVendor: true });
    if (parts.length === 0)
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    return { companyId: req.auth!.companyId, AND: [{ OR: parts }, ownScope(req.auth!)] };
  }

  const roleFilter = (role?: string): Prisma.PartyWhereInput =>
    role === 'vendor'
      ? { isVendor: true }
      : role === 'prospect'
        ? { isProspect: true }
        : role === 'client'
          ? { OR: [{ isClient: true }, { isProspect: true }] }
          : {};

  function listWhere(req: Request): Prisma.PartyWhereInput {
    const q = req.query as Record<string, string | undefined>;
    const search = q.search?.trim();
    return {
      AND: [
        visible(req),
        roleFilter(q.role),
        q.needsReview === 'true' ? { needsReview: true } : {},
        q.active === 'false' ? { active: false } : q.active === 'all' ? {} : { active: true },
        q.ownerId ? { ownerId: Number(q.ownerId) } : {},
        q.tag ? { tags: { some: { tag: { name: q.tag } } } } : {},
        search
          ? {
              OR: [
                { name: { contains: search } },
                { tin: { contains: search } },
                {
                  contacts: {
                    some: {
                      OR: [
                        { name: { contains: search } },
                        { email: { contains: search } },
                        { phone: { contains: search.replace(/^0/, '+256') } },
                      ],
                    },
                  },
                },
              ],
            }
          : {},
      ],
    };
  }

  const summary = (p: Prisma.PartyGetPayload<{ include: typeof partyInclude }>) => {
    const primary = p.contacts.find((c) => c.isPrimary) ?? p.contacts[0];
    return {
      id: p.id,
      name: p.name,
      type: p.type,
      isClient: p.isClient,
      isProspect: p.isProspect,
      isVendor: p.isVendor,
      needsReview: p.needsReview,
      tin: p.tin,
      currency: p.currency,
      contactName: primary?.name ?? null,
      email: primary?.email ?? null,
      phone: primary?.phone ?? null,
      tags: p.tags.map((t) => t.tag.name).sort(),
      ownerId: p.ownerId,
      active: p.active,
      updatedAt: p.updatedAt,
    };
  };

  router.get(
    '/parties',
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const sortKey = ['name', 'createdAt', 'updatedAt'].includes(String(req.query.sort))
        ? String(req.query.sort)
        : 'name';
      const dir = req.query.dir === 'desc' ? 'desc' : 'asc';
      const where = listWhere(req);
      const [total, rows] = await Promise.all([
        db.party.count({ where }),
        db.party.findMany({
          where,
          include: partyInclude,
          orderBy: { [sortKey]: dir },
          skip,
          take,
        }),
      ]);
      res.json({ items: rows.map(summary), total, page, pageSize });
    }),
  );

  router.get(
    '/parties/search',
    asyncHandler(async (req, res) => {
      const q = String(req.query.q ?? '').trim();
      const where: Prisma.PartyWhereInput = {
        AND: [
          visible(req),
          roleFilter(String(req.query.role ?? '')),
          { active: true },
          q ? { name: { contains: q } } : {},
        ],
      };
      const rows = await db.party.findMany({
        where,
        orderBy: { name: 'asc' },
        take: 20,
        include: { contacts: { take: 1, orderBy: { isPrimary: 'desc' } } },
      });
      res.json({
        items: rows.map((p) => ({
          id: p.id,
          label: p.name,
          description:
            [
              p.tin ? `TIN ${p.tin}` : null,
              p.contacts[0]?.email ?? null,
              p.needsReview ? 'Incomplete' : null,
            ]
              .filter(Boolean)
              .join(' · ') || undefined,
        })),
      });
    }),
  );

  router.get(
    '/parties/duplicates',
    asyncHandler(async (req, res) => {
      visible(req);
      const q = parse(
        z.object({
          name: z.string().optional(),
          tin: z.string().optional(),
          email: z.string().optional(),
          phone: z.string().optional(),
          excludeId: z.coerce.number().optional(),
        }),
        req.query,
      );
      res.json({ items: await findDuplicates(db, req.auth!.companyId, q) });
    }),
  );

  router.get(
    '/parties/export.csv',
    asyncHandler(async (req, res) => {
      const role = String(req.query.role ?? 'client');
      need(req, role === 'vendor' ? `${VENDOR}.export` : `${CLIENT}.export`);
      const where = listWhere(req);
      const header = [
        'name',
        'type',
        'kind',
        'tin',
        'vat_registered',
        'vat_number',
        'contact_name',
        'email',
        'phone',
        'whatsapp',
        'address',
        'city',
        'country',
        'website',
        'industry',
        'currency',
        'payment_terms_days',
        role === 'vendor' ? 'wht_applicable' : 'credit_limit',
        role === 'vendor' ? 'payment_details' : 'wht_agent',
        'tags',
        'notes',
        'needs_review',
      ];
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader(
        'Content-Disposition',
        `attachment; filename="${role === 'vendor' ? 'vendors' : 'clients'}.csv"`,
      );
      res.write('﻿' + csvLine(header) + '\r\n');
      let count = 0;
      let cursor = 0;
      // Streamed in pages so large lists never sit in memory.
      for (;;) {
        const rows = await db.party.findMany({
          where,
          include: partyInclude,
          orderBy: { id: 'asc' },
          take: 500,
          ...(cursor ? { cursor: { id: cursor }, skip: 1 } : {}),
        });
        if (rows.length === 0) break;
        for (const p of rows) {
          const c = p.contacts.find((x) => x.isPrimary) ?? p.contacts[0];
          res.write(
            csvLine([
              p.name,
              p.type,
              p.isProspect ? 'prospect' : p.isVendor && !p.isClient ? 'vendor' : 'client',
              p.tin,
              p.vatRegistered ? 'yes' : 'no',
              p.vatNumber,
              c?.name,
              c?.email,
              c?.phone,
              c?.whatsappNumber,
              p.address,
              p.city,
              p.country,
              p.website,
              p.industry,
              p.currency,
              p.paymentTermsDays,
              role === 'vendor'
                ? p.whtApplicable
                  ? 'yes'
                  : 'no'
                : (p.creditLimit?.toFixed(0) ?? ''),
              role === 'vendor' ? p.paymentDetails : p.whtAgent ? 'yes' : 'no',
              p.tags.map((t) => t.tag.name).join(';'),
              p.notes,
              p.needsReview ? 'yes' : 'no',
            ]) + '\r\n',
          );
          count += 1;
        }
        cursor = rows[rows.length - 1]!.id;
      }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: role === 'vendor' ? 'vendors' : 'clients',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: role === 'vendor' ? 'vendors' : 'clients',
        after: { rows: count, filters: req.query },
      });
      res.end(); // logged first so a client that reads the whole file can rely on the audit entry
    }),
  );

  router.get(
    '/parties/:id',
    asyncHandler(async (req, res) => {
      const party = await db.party.findFirst({
        where: { AND: [{ id: Number(req.params.id) }, visible(req)] },
        include: partyInclude,
      });
      if (!party) throw new HttpError(404, 'NOT_FOUND', 'Not found');
      const owner = party.ownerId
        ? await db.user.findUnique({
            where: { id: party.ownerId },
            select: { id: true, name: true },
          })
        : null;
      res.json({
        ...partyView(party),
        owner,
        outstanding: '0.0000',
        documents: { quotations: 0, invoices: 0, receipts: 0 },
      });
    }),
  );

  /** Role permissions for the roles a body asks for. */
  const requireRoleCreate = (
    req: Request,
    roles: { isClient?: boolean; isProspect?: boolean; isVendor?: boolean },
    action: 'create' | 'edit',
  ) => {
    if (roles.isClient || roles.isProspect) need(req, `${CLIENT}.${action}`);
    if (roles.isVendor) need(req, `${VENDOR}.${action}`);
  };

  async function validateCustom(req: Request, values: PartyInput['customFields'] | undefined) {
    const defs = await db.customFieldDef.findMany({
      where: { companyId: req.auth!.companyId, entity: 'party' },
    });
    const problem = customFieldError(defs, values);
    if (problem) throw new HttpError(400, 'INVALID_CUSTOM_FIELD', problem);
  }

  router.post(
    '/parties',
    asyncHandler(async (req, res) => {
      const input = parse(partyInputSchema, req.body);
      requireRoleCreate(req, input, 'create');
      await validateCustom(req, input.customFields);
      const party = await db.$transaction(async (tx) => {
        const created = await createParty(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          input,
        });
        await audit(tx, actor(req), {
          action: 'party.created',
          entity: 'party',
          entityId: created.id,
          after: partyView(created),
        });
        return created;
      });
      res.status(201).json(partyView(party));
    }),
  );

  /** Create in place (PLAN.md 7.9.3): name only; flagged Incomplete for the accountant to review. */
  router.post(
    '/parties/quick',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          name: z.string().trim().min(2).max(200),
          role: z.enum(['client', 'prospect', 'vendor']).default('client'),
          email: z.string().trim().email().optional(),
          phone: z.string().trim().max(30).optional(),
        }),
        req.body,
      );
      const roles = {
        isClient: body.role === 'client',
        isProspect: body.role === 'prospect',
        isVendor: body.role === 'vendor',
      };
      requireRoleCreate(req, roles, 'create');
      const input = partyInputSchema.parse({
        name: body.name,
        ...roles,
        contacts:
          body.email || body.phone
            ? [
                {
                  name: body.name,
                  email: body.email ?? '',
                  phone: body.phone ?? null,
                  receivesDocuments: !!body.email,
                },
              ]
            : [],
      });
      const party = await db.$transaction(async (tx) => {
        const created = await createParty(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          input,
          needsReview: true,
        });
        await audit(tx, actor(req), {
          action: 'party.created_in_place',
          entity: 'party',
          entityId: created.id,
          after: { name: created.name, role: body.role },
        });
        return created;
      });
      res.status(201).json({ id: party.id, label: party.name, description: 'Incomplete' });
    }),
  );

  router.patch(
    '/parties/:id',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const input = parse(partyInputSchema.innerType().partial(), req.body);
      const before = await db.party.findFirst({
        where: { AND: [{ id }, visible(req)] },
        include: partyInclude,
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Not found');
      requireRoleCreate(req, before, 'edit');
      requireRoleCreate(
        req,
        { isClient: input.isClient, isProspect: input.isProspect, isVendor: input.isVendor },
        'edit',
      );
      if (input.customFields !== undefined) await validateCustom(req, input.customFields);
      const merged = {
        isClient: input.isClient ?? before.isClient,
        isProspect: input.isProspect ?? before.isProspect,
        isVendor: input.isVendor ?? before.isVendor,
      };
      if (!merged.isClient && !merged.isProspect && !merged.isVendor)
        throw new HttpError(
          400,
          'VALIDATION_ERROR',
          'A record must stay a client, prospect or vendor',
        );
      const after = await db.$transaction(async (tx) => {
        const updated = await updateParty(tx, {
          companyId: before.companyId,
          userId: req.auth!.userId,
          id,
          input: input as never,
        });
        await audit(tx, actor(req), {
          action: 'party.updated',
          entity: 'party',
          entityId: id,
          before: partyView(before),
          after: partyView(updated),
        });
        return updated;
      });
      res.json(partyView(after));
    }),
  );

  router.post(
    '/parties/:id/convert-to-client',
    asyncHandler(async (req, res) => {
      need(req, `${CLIENT}.edit`);
      const id = Number(req.params.id);
      const before = await db.party.findFirst({
        where: { AND: [{ id }, visible(req)] },
        include: partyInclude,
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Not found');
      if (!before.isProspect)
        throw new HttpError(409, 'NOT_PROSPECT', 'Only prospects can be converted');
      const after = await db.$transaction(async (tx) => {
        const updated = await tx.party.update({
          where: { id },
          data: { isClient: true, isProspect: false, updatedBy: req.auth!.userId },
          include: partyInclude,
        });
        await audit(tx, actor(req), {
          action: 'party.converted_to_client',
          entity: 'party',
          entityId: id,
          before: { isProspect: true },
          after: { isClient: true },
        });
        return updated;
      });
      res.json(partyView(after));
    }),
  );

  router.post(
    '/parties/:id/review-complete',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const before = await db.party.findFirst({
        where: { AND: [{ id }, visible(req)] },
        include: partyInclude,
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Not found');
      requireRoleCreate(req, before, 'edit');
      const after = await db.$transaction(async (tx) => {
        const updated = await tx.party.update({
          where: { id },
          data: { needsReview: false, updatedBy: req.auth!.userId },
          include: partyInclude,
        });
        await audit(tx, actor(req), {
          action: 'party.review_completed',
          entity: 'party',
          entityId: id,
        });
        return updated;
      });
      res.json(partyView(after));
    }),
  );

  router.get(
    '/tags',
    asyncHandler(async (req, res) => {
      const tags = await db.tag.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: { name: 'asc' },
      });
      res.json({ items: tags.map((t) => t.name) });
    }),
  );

  // ───── custom field definitions ─────
  router.get(
    '/custom-fields',
    asyncHandler(async (req, res) => {
      const entity = String(req.query.entity ?? 'party');
      res.json({
        items: await db.customFieldDef.findMany({
          where: { companyId: req.auth!.companyId, entity },
          orderBy: { id: 'asc' },
        }),
      });
    }),
  );

  router.post(
    '/custom-fields',
    asyncHandler(async (req, res) => {
      need(req, 'settings.template.edit');
      const body = parse(
        z.object({
          entity: z.enum(['party', 'item']).default('party'),
          key: z
            .string()
            .trim()
            .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Use lowercase letters, digits and underscores'),
          label: z.string().trim().min(2).max(100),
          type: z.enum(['text', 'number', 'date', 'select']).default('text'),
          options: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
          required: z.boolean().default(false),
        }),
        req.body,
      );
      if (body.type === 'select' && !body.options?.length)
        throw new HttpError(400, 'VALIDATION_ERROR', 'A select field needs options');
      const dup = await db.customFieldDef.findUnique({
        where: {
          companyId_entity_key: {
            companyId: req.auth!.companyId,
            entity: body.entity,
            key: body.key,
          },
        },
      });
      if (dup) throw new HttpError(409, 'KEY_TAKEN', 'A field with this key already exists');
      const created = await db.customFieldDef.create({
        data: { companyId: req.auth!.companyId, ...body },
      });
      await audit(db, actor(req), {
        action: 'custom_field.created',
        entity: 'custom_field',
        entityId: created.id,
        after: body,
      });
      res.status(201).json(created);
    }),
  );

  router.delete(
    '/custom-fields/:id',
    asyncHandler(async (req, res) => {
      need(req, 'settings.template.edit');
      const def = await db.customFieldDef.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId },
      });
      if (!def) throw new HttpError(404, 'NOT_FOUND', 'Not found');
      await db.customFieldDef.delete({ where: { id: def.id } });
      await audit(db, actor(req), {
        action: 'custom_field.deleted',
        entity: 'custom_field',
        entityId: def.id,
        before: def,
      });
      res.status(204).end();
    }),
  );

  return router;
}
